import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { WithDisposable } from '@blocksuite/global/lit';
import { ArrowDownSmallIcon, PlusIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed } from '@preact/signals-core';
import { html } from 'lit';
import { property } from 'lit/decorators.js';

import { TableViewAreaSelection } from '../../../selection';
import type { VirtualTableViewUILogic } from '../../table-view-ui-logic';
import type { TableGridGroup } from '../../types';
import * as styles from './group-footer-css';

// Safety cap so an accidental large number does not insert thousands of rows.
const MAX_ADD_ROWS_AT_ONCE = 100;

export class TableGroupFooter extends WithDisposable(ShadowlessElement) {
  @property({ attribute: false })
  accessor tableViewLogic!: VirtualTableViewUILogic;

  @property({ attribute: false })
  accessor gridGroup!: TableGridGroup;

  group$ = computed(() => {
    const groups =
      this.tableViewLogic.groupTrait$.value?.groupsDataList$.value ?? [];
    return groups
      .filter((group): group is NonNullable<typeof group> => group != null)
      .find(g => g.key === this.gridGroup.groupId);
  });

  get selectionController() {
    return this.tableViewLogic.selectionController;
  }

  get tableViewManager() {
    return this.tableViewLogic.view;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(styles.groupFooter);
    this.disposables.addFromEvent(this, 'mouseenter', () => {
      this.gridGroup.data.footerHover$.value = true;
    });
    this.disposables.addFromEvent(this, 'mouseleave', () => {
      this.gridGroup.data.footerHover$.value = false;
    });
  }

  private readonly addRows = (count: number) => {
    const group = this.group$.value;
    const total = Math.min(
      Math.max(1, Math.floor(count)),
      MAX_ADD_ROWS_AT_ONCE
    );
    let firstRowId: string | undefined;
    for (let i = 0; i < total; i++) {
      const rowId = this.tableViewManager.rowAdd('end', group?.key);
      if (i === 0) {
        firstRowId = rowId;
      }
    }
    this.requestUpdate();

    if (!firstRowId) return;
    const targetRowId = firstRowId;

    requestAnimationFrame(() => {
      const rowIndex = this.selectionController.getRow(group?.key, targetRowId)
        ?.rowIndex$.value;
      if (rowIndex == null) return;
      const index = this.tableViewManager.properties$.value.findIndex(
        v => v.type$.value === 'title'
      );

      this.selectionController.selection = undefined;

      requestAnimationFrame(() => {
        this.selectionController.selection = TableViewAreaSelection.create({
          groupKey: group?.key,
          focus: {
            rowIndex: rowIndex,
            columnIndex: index,
          },
          isEditing: true,
        });
      });
    });
  };

  private readonly clickAddRow = () => {
    this.addRows(1);
  };

  private readonly openAddRowsMenu = (e: MouseEvent) => {
    // Prevent the wrapper's click handler from also adding a single row.
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    popMenu(popupTargetFromElement(target), {
      options: {
        title: { text: 'Add records' },
        items: [
          menu.input({
            initialValue: '1',
            placeholder: 'Number of records',
            onComplete: text => {
              const count = Number.parseInt(text, 10);
              if (Number.isFinite(count) && count > 0) {
                this.addRows(count);
              }
            },
          }),
          menu.action({
            name: 'Add 5 records',
            select: () => this.addRows(5),
          }),
          menu.action({
            name: 'Add 10 records',
            select: () => this.addRows(10),
          }),
          menu.action({
            name: 'Add 25 records',
            select: () => this.addRows(25),
          }),
        ],
      },
    });
  };

  override render() {
    return html`
      ${this.tableViewManager.readonly$.value
        ? null
        : html`
            <div
              class="${styles.addRowWrapper} dv-hover"
              @click="${this.clickAddRow}"
            >
              <div
                class="${styles.addRowButton} dv-icon-16"
                data-test-id="affine-database-add-row-button"
                role="button"
              >
                ${PlusIcon()}<span class="${styles.addRowText}"
                  >New Record</span
                >
              </div>
              <div
                class="${styles.addRowCountButton} dv-icon-16 dv-hover"
                data-test-id="affine-database-add-rows-count-button"
                role="button"
                @click="${this.openAddRowsMenu}"
              >
                ${ArrowDownSmallIcon()}
              </div>
            </div>
          `}
      <affine-database-virtual-column-stats
        .view="${this.tableViewManager}"
        .group="${this.group$.value}"
      ></affine-database-virtual-column-stats>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'virtual-table-group-footer': TableGroupFooter;
  }
}
